import { createContext } from '@shared/utils/createContext';
import type { ExternalAccount, PayoutFee } from '@shared/components/PayoutDialog/PayoutDialog.types';

export type PayoutAccountInfo = Readonly<{
  status: 'onboarding' | 'readyToUse' | 'pending' | 'rejected';
  currencyCode: string;
  countryCode?: string;
  statusDetails: string;
  onboardingUrl: string;
  serviceDashboardUrl: string;
  externalAccountLast4: string | null;
  balance?: PayoutAccountBalance;
  withdrawals: ReadonlyArray<PayoutAccountWithdrawal>;
  defaultExternalAccount?: ExternalAccount | null;
  standardPayoutFee?: PayoutFee | null;
  instantPayoutFee?: PayoutFee | null;
  payoutThrottleDelayMs?: number | null;
  nextWithdrawalAvailableAt?: { readonly milliseconds: number } | null;
  payoutLimitInMinorUnits?: number | null;
}>;

export type PayoutAccountBalance = Readonly<{
  availablePending: CommerceMonetaryValue;
  available: CommerceMonetaryValue;
  received: CommerceMonetaryValue;
  withdrawn: CommerceMonetaryValue;
  withdrawnPending: CommerceMonetaryValue;
  /** Cash fund spent in hotel checkout. */
  travelSpent?: CommerceMonetaryValue;
}>;

export type PayoutAccountWithdrawal = Readonly<{
  id: string;
  status: string; // RegistryPayoutAccountWithdrawalStatus
  totalValue?: CommerceMonetaryValue;
}>;

export type CommerceMonetaryValue = Readonly<{
  currencyCode: string;
  valueInMinorUnits: number;
  valueInMajorUnits: number;
}>;

type CreditCardSetupContext = Readonly<{
  setSetupCreditCard: (nextValue: string | null) => void;
  fetchingPayoutInfo: boolean;
  /** True once the initial payout account lookup has completed (success or error). False while unknown — callers should treat "no account" as undetermined, not as "no active account", until this flips true. */
  payoutInfoChecked: boolean;
  /** Returns 'true' when there was a successful withdrawal in current session */
  hasDoneWithdrawal: boolean;
  hasFailedWithdrawals: boolean;
  hasPendingWithdrawals: boolean;
  canManagePayouts: boolean;
  payoutAccountInfo: PayoutAccountInfo | null;
  maxAttemptsReached: boolean;
  initKYCOnboarding: () => void;
  initKYCOnboardingFromRefund: (returnRoute: string) => void;
  /** True once the resolved cash-fund locale is loaded — callers like CreditCardSetupRoute should wait for this before firing `initKYCOnboarding`. */
  kycReady: boolean;
  /** True when the registry currency maps to a Stripe-supported cash-fund country. */
  isLocaleSupported: boolean;
  /** True only when the payout account (or, before setup, the registry currency) is US/USD. Get Cash Equivalent is US-only for now. */
  isGetCashEquivalentSupported: boolean;
  /** Alpha-3 country code resolved from the registry currency (e.g. 'USA', 'GBR'). Defaults to 'USA' when unsupported. Used to vary copy by payout country, not browser locale. */
  resolvedCashFundCountryCode: string;
  /** ISO-4217 currency code resolved from the registry currency (e.g. 'USD', 'GBP'). Defaults to 'USD' when unsupported. */
  resolvedCashFundCurrencyCode: string;
  /** True when the most recent `createPayoutAccount` attempt errored (network or RegistryErrorResult). Lets route-level loaders break out of infinite waits. Resets on each new attempt. */
  hasKYCFailed: boolean;
  initKYCLaterOnboarding: (firstName: string, lastName: string, currencyCode: string, countryCode: string) => Promise<boolean>;
  /** Refetches the payout account. Pass `{ forceRefresh: true }` only when the couple may have just
   * changed the account in Stripe (i.e. the bank-account dialog closing) -- it forces a live Stripe
   * read instead of trusting the webhook-fed copy, and costs a real round trip. Returns the query
   * promise so a forced caller can await its settlement (see `usePayoutMethodUpdatedToast`). */
  fetchPayoutInfo: (opts?: { forceRefresh?: boolean }) => Promise<unknown>;
  onOpenPayoutDialog: () => void;
  initPayoutAccountLoading: boolean;
  initKYCLaterLoading: boolean;
}>;

export const [Provider, useCreditCardSetupContext] = createContext<CreditCardSetupContext>({ name: 'CreditCardSetUpContext' });
